import {
  WSActions,
  WSControlGroup,
  WSElement,
  WSInfoBox,
  WSList,
  WSPanel,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useMutationCreatePlanEnrollment } from "../../../../../query/benefits";
import { ErrorAlert } from "@components/ErrorAlert";
import { PGLI_KEY, PLAN_DETAILS } from "../../../utils/constants";
import { WS_LINKS } from "../../../../../types/wsLinks";
import { FlowStep } from "../../../../../shared/components/FlowSwitch/types";

const { monthlyPremiumFormatted, initiationFeeFormatted } =
  PLAN_DETAILS[PGLI_KEY];

export const StepCertify: React.FC<FlowStep> = props => {
  // TODO(v3-blocker): no implemented v3 write enrolls a contractor in a Wingspan
  // insurance plan (see the mutation hook), so "Confirm & enroll" always fails
  // with `NotMigrated`.
  const [createEnrollment, createEnrollmentMeta] =
    useMutationCreatePlanEnrollment();

  const [attestations, setAttestations] = React.useState({});

  const assertationsLabels: React.ReactNode[] = [
    <WSText.ParagraphSm color="gray600">
      <b>Work type confirmation:</b> I attest that the work I perform is
      consistent with the types of work (Independent Claims Adjusting) covered
      under the Wingspan Professional Liability and General Liability Master
      Policy.
    </WSText.ParagraphSm>,
    <WSText.ParagraphSm color="gray600">
      <b>Consent to payment terms:</b> I understand and agree to the premium
      costs — a one-time {initiationFeeFormatted} initiation fee and a recurring{" "}
      {monthlyPremiumFormatted} monthly premium. I authorize Wingspan to deduct
      these premiums from my payments or charge my payment method on file to
      maintain my coverage.
    </WSText.ParagraphSm>,
    <WSText.ParagraphSm color="gray600">
      <b>Policy review:</b> I have reviewed and accept the terms and conditions
      of the Master Policy.{" "}
      <a href={WS_LINKS.wingspanPLGLPolicy} target="_blank" rel="noreferrer">
        View policy document.
      </a>
    </WSText.ParagraphSm>
  ];

  const assertationOptions = assertationsLabels.map((label, index) => ({
    label,
    value: `${index + 1}`
  }));

  const isReadyToSubmit =
    Object.values(attestations).filter(Boolean).length ===
    assertationsLabels.length;

  return (
    <WSPanel>
      <WSList gap="L">
        <WSElement>
          <WSText.Paragraph weight="medium" mb="XL">
            Eligibility & certification
          </WSText.Paragraph>
          <WSText.ParagraphSm color="gray500">
            Please review the eligibility requirements and certify the following
            statements to enroll.
          </WSText.ParagraphSm>
        </WSElement>

        <WSList gap="M">
          <WSInfoBox icon="user-check" title="Eligibility requirements:">
            To be eligible for this specific policy, you must be operating as an
            Independent Claims Adjuster.
          </WSInfoBox>

          <WSInfoBox icon="accept" title="You agree to:">
            (Checkboxes required for all items below)
            <WSControlGroup
              type="checkbox"
              value={attestations}
              name="attestations"
              onChange={setAttestations}
              size="S"
              mt="M"
              options={assertationOptions}
            />
          </WSInfoBox>
        </WSList>

        <WSText.ParagraphSm color="gray500">
          Wingspan Solutions LLC is not a licensed insurance agency and is not
          the insurer. This coverage is underwritten by Evanston Insurance
          Company (a Markel company) via the Master Policy issued to the
          Professional Industry Purchasing Group.
        </WSText.ParagraphSm>

        <ErrorAlert error={createEnrollmentMeta.error} context="Other" />
        <WSActions
          alignment="fill"
          buttons={[
            {
              kind: "Tertiary",
              onClick: props.onBack,
              label: "Back"
            },
            {
              disabled: !isReadyToSubmit,
              label: "Confirm & enroll",
              type: "submit",
              onAsyncClick: async () => {
                await createEnrollment(
                  {
                    planType:
                      "WingspanProfessionalLiabilityAndGeneralLiabilityInsurance"
                  },
                  {
                    onSuccess: () => {
                      props.onNext?.();
                    }
                  }
                );
              }
            }
          ]}
        />
      </WSList>
    </WSPanel>
  );
};
